import { inject } from '@angular/core';
import { type CanActivateFn, Router } from '@angular/router';

import { AuthService } from './auth.service';

/**
 * Admin role guard — checks that the user is authenticated AND has role === 'admin'.
 *
 * Critical: Do NOT use authGuard for admin routes — it only checks isAuthenticated().
 * This guard adds the role check to prevent customers from reaching admin pages.
 * Backend Casbin is the authoritative gate; this guard prevents unnecessary 403 round-trips.
 *
 * T-3-17: Customer logs in via admin login form and reaches admin pages — mitigated here.
 */
export const adminGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  await authService.ensureAuthReady();

  if (authService.isAuthenticated() && authService.currentUser()?.role === 'admin') {
    return true;
  }

  return router.createUrlTree(['/login']);
};

/**
 * Prevents authenticated admins from idling on /login.
 *
 * Waits for bootstrap to complete before checking auth state so a
 * page-refresh to /login with an existing valid admin token redirects
 * immediately rather than flashing the form.
 *
 * Note: if a customer token is found (role !== 'admin'), the user is NOT
 * redirected into the admin shell — they are allowed to see the login form
 * so they can sign in with the correct admin credentials.
 */
export const adminPublicGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  await authService.ensureAuthReady();

  if (authService.isAuthenticated() && authService.currentUser()?.role === 'admin') {
    return router.createUrlTree(['/customers']);
  }

  return true;
};
